import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Scissors, Sparkles, Star, ArrowRight } from 'lucide-react';

// User uploaded videos
const userVideos = [
  'https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/14gid1ny_6c9c273d-0aaa-4ebd-9544-e7100510bbb2.mp4',
  'https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/75idfuq0_8d615dd6-d352-49f1-91eb-08ce5dca24a0.mp4',
  'https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/320t38pp_89d36ab1-5880-4995-aa38-628db155ada3.mp4',
  'https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/va5ligy0_503c6750-6fe9-4faa-8b29-ee3b6e08e048.mp4',
  'https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/dtojd7af_616f275d-7d1c-4c80-905a-b46558e82034.mp4',
];

const featuredServices = [
  {
    title: 'Luxury Braids',
    description: 'Knotless, Box Braids, Cornrows & more',
    icon: Sparkles,
    media: 'https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/slaxxxfj_9490abde-57ed-445a-ae63-e6fcc513f479.mp4',
    isVideo: true
  },
  {
    title: 'Elite Barber',
    description: 'Precision Fades, Lineups & Styling',
    icon: Scissors,
    media: 'https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/geoz6vot_c74edf8e-f2f6-40f1-94af-b808ec09fcc0.mp4',
    isVideo: true
  },
  {
    title: 'VIP Experience',
    description: 'Celebrity-level service & privacy',
    icon: Star,
    media: 'https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/7yscu7rn_b5d5387b-a162-426e-b138-db74c783fb11.mp4',
    isVideo: true
  }
];

export const Home = () => {
  return (
    <div className="page-transition">
      {/* Hero Section */}
      <section className="video-hero relative min-h-screen flex items-center justify-center">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={userVideos[0]} type="video/mp4" />
        </video>
        <div className="video-overlay" />
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-script text-6xl sm:text-7xl md:text-8xl lg:text-9xl text-gold text-shadow mb-6"
          >
            Braids By Mel
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-serif text-xl md:text-2xl text-[#F5F5F0] text-shadow mb-4 italic"
          >
            Authentic African Braids & Haircut Designs
          </motion.p>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-sans text-[#A3A3A3] text-base md:text-lg max-w-2xl mx-auto mb-10"
          >
            The Philippines' premier Black-owned salon. Welcoming all races, ages & nationalities. 
            Experience real African braiding techniques in Makati City.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/contact"
              className="btn-gold px-10 py-4 rounded-full font-serif text-lg tracking-wide inline-flex items-center justify-center gap-2 group"
              data-testid="hero-book-btn"
            >
              Book Your Session
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/gallery"
              className="border border-white/30 text-white px-10 py-4 rounded-full font-serif text-lg tracking-wide hover:bg-white/10 transition-all inline-flex items-center justify-center"
              data-testid="hero-gallery-btn"
            >
              View Our Work
            </Link>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-gold/50 rounded-full flex justify-center">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="w-1.5 h-3 bg-gold rounded-full mt-2"
            />
          </div>
        </motion.div>
      </section>

      {/* Featured Services */}
      <section className="py-24 px-6 bg-[#050505]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-script text-5xl md:text-6xl text-gold mb-4">Our Signature Services</h2>
            <p className="font-sans text-[#A3A3A3] text-lg max-w-2xl mx-auto">
              Crafted for those who demand excellence
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {featuredServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="service-card bg-surface border border-gold/20 rounded-2xl overflow-hidden group"
              >
                <div className="aspect-[4/3] overflow-hidden relative bg-surface">
                  {service.isVideo ? (
                    <video
                      src={service.media}
                      className="w-full h-full object-cover"
                      loop
                      muted
                      playsInline
                      autoPlay
                    />
                  ) : (
                    <img
                      src={service.media}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <service.icon className="w-6 h-6 text-pink" />
                    <h3 className="font-serif text-2xl text-gold-light">{service.title}</h3>
                  </div>
                  <p className="text-[#A3A3A3] font-sans">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-12"
          >
            <Link
              to="/services"
              className="btn-gold px-8 py-3 rounded-full font-serif inline-flex items-center gap-2 group"
              data-testid="view-all-services-btn"
            >
              View All Services
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Video Showcase */}
      <section className="py-24 px-6 bg-surface">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-script text-5xl md:text-6xl text-gold mb-4">Our Latest Work</h2>
            <p className="font-sans text-[#A3A3A3] text-lg">
              See why celebrities choose BraidsByMel
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {userVideos.map((video, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="gallery-video aspect-[9/16] rounded-xl overflow-hidden border border-gold/20"
              >
                <video
                  src={video}
                  className="w-full h-full object-cover"
                  loop
                  muted
                  playsInline
                  autoPlay
                  preload="auto"
                />
                />
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center mt-12"
          >
            <Link
              to="/gallery"
              className="btn-gold px-8 py-3 rounded-full font-serif inline-flex items-center gap-2 group"
              data-testid="view-gallery-btn"
            >
              Explore Gallery
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Our Studio Preview */}
      <section className="py-24 px-6 bg-[#050505]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-script text-4xl md:text-6xl text-gold mb-6">Visit Our Studio</h2>
              <p className="font-sans text-[#A3A3A3] text-lg leading-relaxed mb-6">
                Step into luxury at BraidsByMel. Our stunning pink and black themed studio is designed 
                to make you feel like a star from the moment you walk through our doors. Professional 
                styling stations, comfortable seating, and an ambiance that screams excellence.
              </p>
              <Link
                to="/about"
                className="btn-gold px-8 py-3 rounded-full font-serif inline-flex items-center gap-2 group"
                data-testid="view-studio-btn"
              >
                See Our Space
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="rounded-2xl overflow-hidden border border-gold/20">
                <img 
                  src="https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/7ykelztm_87dbeaed-bf54-4f25-a94b-f9fdca0a8a7a.jpg"
                  alt="BraidsByMel Styling Stations"
                  className="w-full h-full object-cover aspect-square hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="rounded-2xl overflow-hidden border border-gold/20 mt-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/uwhj8wc4_3116297e-ec99-47ad-9e4c-9e427d3bc936.jpg"
                  alt="BraidsByMel Reception"
                  className="w-full h-full object-cover aspect-square hover:scale-105 transition-transform duration-500"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-pink/10 via-transparent to-gold/10" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-script text-5xl md:text-7xl text-gold mb-6"
          >
            Ready for Your Transformation?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-sans text-[#A3A3A3] text-lg mb-10 max-w-2xl mx-auto"
          >
            Join our exclusive clientele and experience the luxury that A-list celebrities trust.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Link
              to="/contact"
              className="btn-gold px-12 py-4 rounded-full font-serif text-xl inline-flex items-center gap-3 group"
              data-testid="cta-book-btn"
            >
              Book Your VIP Session
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
